import { useState, createContext, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { MessageAlertCircle, MessageCheckCircle, MessageXCircle } from '~/icons';

type NotificationType = 'success' | 'warning' | 'error';

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(-10px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

const colorsType = {
  warning: '#d97706',
  error: '#c53030',
  success: '#2f855a',
};

const PopupContainer = styled.div<{ type: NotificationType, message: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  position: fixed;
  bottom: 30px;
  left: ${({ message }) => `calc(50% - (${message.length}ch / 2))`};
  transform: translateX(-50%);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  animation: ${fadeInOut} 3s ease-in-out forwards;
  color: ${({ type }) => (colorsType[type])};
  z-index: 10000000000000;
  background-color: white;
`;

const icons = {
  success: MessageCheckCircle,
  warning: MessageAlertCircle,
  error: MessageXCircle,
};

const NotificationContext = createContext<{ notify:(message: string, type: NotificationType) => void } | undefined>(undefined);

export const NotificationPopUpProvider = ({ children }) => {
  const [notifications, setNotifications] = useState<{ id: number; message: string; type: NotificationType }[]>([]);

  const removeNotification = (id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const notify = (message: string, type: NotificationType) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => removeNotification(id), 3000);
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      {notifications.map(({ id, message, type }) => {
        const Icon = icons[type];
        return (
          <PopupContainer key={id} type={type} message={message}>
            <Icon color={colorsType[type]} />
            <span>{message}</span>
          </PopupContainer>
        );
      })}
    </NotificationContext.Provider>
  );
};

export const useNotify = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext deve ser usado dentro de um CardProvider');
  }
  return context;
};

// Usage example in a component
// import { useNotify } from '~/contexts/notificationPopUp';
// const { notify } = useNotify();

// notify('Alterações realizadas com sucesso!', 'success');
// notify('Aviso!', 'warning');
// notify('Erro!', 'error');
